import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Briefcase, DollarSign, Lock, Trophy, Clock } from "lucide-react";
import { RfpThreadPanel } from "@/components/RfpThreadPanel";

export default function VendorRfpPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ amount: "", proposal: "", eta_days: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/rfp/mine");
      setItems(data.items || []);
    } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openBid = (r) => {
    setSelected(r);
    setForm({
      amount: r.my_bid?.amount ?? "",
      proposal: r.my_bid?.proposal ?? "",
      eta_days: r.my_bid?.eta_days ?? "",
    });
  };

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      await api.post(`/rfp/${selected.id}/bid`, {
        amount: Number(form.amount),
        proposal: form.proposal || null,
        eta_days: form.eta_days ? Number(form.eta_days) : null,
      });
      toast.success("Sealed bid submitted");
      setSelected(null);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Procurement"
        title="Invited RFPs"
        subtitle="You've been hand-picked. Sealed bids — only the admin sees them until awarded."
      />
      <div className="space-y-3">
        {items.map((r) => (
          <Card key={r.id} className="border-white/10 bg-card p-5" data-testid={`vrfp-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={
                    r.status === "open" ? "border-orange-500/40 text-orange-300 bg-orange-500/10"
                    : r.status === "awarded" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                    : "border-white/10 text-zinc-400"
                  }>
                    {r.status}
                  </Badge>
                  <div className="overline">{r.category}</div>
                  {r.budget_ceiling ? <div className="text-xs text-zinc-500">Budget ≤ ${Number(r.budget_ceiling).toLocaleString()}</div> : null}
                  {r.deadline ? <div className="text-xs text-zinc-500 flex items-center gap-1"><Clock className="h-3 w-3" /> Deadline {r.deadline}</div> : null}
                </div>
                <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-emerald-400" /> {r.title}
                </div>
                <div className="text-sm text-zinc-400 mt-1">{r.property_name}</div>
                {r.scope && <div className="text-sm text-zinc-300 mt-2"><b>Scope:</b> {r.scope}</div>}
                {r.description && <div className="text-sm text-zinc-400 mt-1">{r.description}</div>}
                {r.status === "awarded" && r.awarded_vendor_id && (
                  <div className="text-xs text-emerald-300 mt-2 flex items-center gap-1">
                    <Trophy className="h-3 w-3" /> Awarded {r.awarded_at ? new Date(r.awarded_at).toLocaleDateString() : ""}
                  </div>
                )}
              </div>
              <div className="shrink-0 flex flex-col items-end gap-2">
                {r.my_bid ? (
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Your sealed bid</div>
                    <div className="text-lg tabular-nums text-emerald-300">${Number(r.my_bid.amount).toFixed(2)}</div>
                    <Badge variant="outline" className="border-white/10 capitalize">{r.my_bid.status}</Badge>
                  </div>
                ) : (
                  <div className="text-[11px] flex items-center gap-1 text-zinc-500"><Lock className="h-3 w-3" /> No bid yet</div>
                )}
                {r.status === "open" && (
                  <Button
                    size="sm"
                    onClick={() => openBid(r)}
                    className="bg-emerald-600 hover:bg-emerald-700 rounded-full"
                    data-testid={`vrfp-bid-${r.id}`}
                  >
                    <DollarSign className="h-4 w-4 mr-1" /> {r.my_bid ? "Update bid" : "Submit bid"}
                  </Button>
                )}
              </div>
            </div>
            <RfpThreadPanel rfp={r} />
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="border-dashed border-white/10 p-10 text-center text-muted-foreground">
            No RFP invites right now. Admins will invite you when large jobs come up.
          </Card>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader><DialogTitle>Sealed bid · {selected?.title}</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-500 flex items-center gap-1 mb-2">
            <Lock className="h-3 w-3" /> Only the admin will see your bid until an award is made.
          </div>
          <div className="space-y-3">
            <div>
              <Label>Amount (USD)</Label>
              <Input data-testid="vrfp-amount" type="number" step="0.01" min="0"
                value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div>
              <Label>ETA (days)</Label>
              <Input data-testid="vrfp-eta" type="number" min="0"
                value={form.eta_days} onChange={(e) => setForm({ ...form, eta_days: e.target.value })}
                placeholder="e.g. 14" />
            </div>
            <div>
              <Label>Proposal</Label>
              <Textarea data-testid="vrfp-proposal" rows={5} value={form.proposal}
                onChange={(e) => setForm({ ...form, proposal: e.target.value })}
                placeholder="What's included, materials, warranty, exclusions" />
            </div>
            <Button data-testid="vrfp-submit" onClick={submit} disabled={saving}
                    className="w-full bg-emerald-600 hover:bg-emerald-700">
              {saving ? "Submitting…" : "Submit sealed bid"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
